<template>
  <div class="reg-split">
    <AuthBrandPanel
      :kick="panel.kick"
      :heading="panel.heading"
      :sub="panel.sub"
      :bullets="panel.bullets"
      :foot="panel.foot"
    />

    <div class="auth-form">
      <div class="af-inner" :style="{ maxWidth: isCompanyStep ? '440px' : '400px' }">
        <!-- step indicator -->
        <div v-if="!closed" class="reg-steps">
          <div class="reg-step" :class="{ on: !isCompanyStep, done: isCompanyStep }">
            <span class="rs-num">{{ isCompanyStep ? '✓' : '1' }}</span> Your account
          </div>
          <div class="reg-line" :class="{ done: isCompanyStep }"></div>
          <div class="reg-step" :class="{ on: isCompanyStep }">
            <span class="rs-num">2</span> Your company
          </div>
        </div>

        <!-- ===== REGISTRATION CLOSED (server answered registration_closed) ===== -->
        <template v-if="closed">
          <div class="big-state">
            <div class="bs-ic warn"><CygnusIcon name="lock" /></div>
            <h3>Registration is closed</h3>
            <p>Yorlin is currently by invitation only. If you have been invited, use the link in your invitation email.</p>
            <div class="resend"><a href="/login">Go to sign in</a></div>
          </div>
        </template>

        <!-- ============================ STEP 1 — ACCOUNT ============================ -->
        <template v-else-if="!isCompanyStep && mode !== 'sent'">
          <div class="af-h">Create your account</div>
          <div class="af-sub">Already have one? <a href="/login">Sign in</a></div>

          <div v-if="banner" class="fbanner err">
            <span class="fb-ic"><CygnusIcon name="warning" /></span>
            <span>{{ banner }}</span>
          </div>

          <form @submit.prevent="submitStart">
            <div class="field">
              <div class="fl">Full name</div>
              <div class="input" :class="{ error: fieldErr === 'name' }">
                <span class="ic"><CygnusIcon name="user" /></span>
                <input v-model.trim="form.name" class="val" type="text" placeholder="Your full name" autocomplete="name" />
              </div>
              <div v-if="fieldErr === 'name'" class="field-err">✕ {{ banner }}</div>
            </div>

            <div class="field">
              <div class="fl">Work email <span class="opt">· we'll verify this</span></div>
              <div class="input" :class="{ error: fieldErr === 'email' }">
                <span class="ic"><CygnusIcon name="envelope" /></span>
                <input v-model.trim="form.email" class="val" type="email" placeholder="you@company.co.uk" autocomplete="email" />
              </div>
            </div>

            <div class="field">
              <div class="fl">Password</div>
              <div class="input" :class="{ error: fieldErr === 'password' }">
                <span class="ic"><CygnusIcon name="lock" /></span>
                <input v-model="form.password" class="val" :type="showPw ? 'text' : 'password'" placeholder="At least 10 characters" autocomplete="new-password" />
                <span class="eye" role="button" tabindex="0" @click="showPw = !showPw" @keyup.enter="showPw = !showPw">
                  <CygnusIcon name="eye" />
                </span>
              </div>
              <div v-if="form.password" class="pw-strength">
                <div class="pw-bars">
                  <i v-for="n in 4" :key="n" :class="barClass(n)"></i>
                </div>
                <div class="pw-lbl">
                  <span>{{ pw.hint }}</span>
                  <b :class="pw.labelClass">{{ pw.label }}</b>
                </div>
              </div>
            </div>

            <div class="checks">
              <label class="chk">
                <span class="box" :class="{ on: form.tc }" @click.prevent="form.tc = !form.tc"><CygnusIcon name="check" v-if="form.tc" /></span>
                <input v-model="form.tc" type="checkbox" class="chk-native" />
                <span>I agree to the <a :href="legal.terms" target="_blank" rel="noopener">Terms of Service</a> and <a :href="legal.fairSending" target="_blank" rel="noopener">Acceptable Use</a>.</span>
              </label>
              <label class="chk">
                <span class="box" :class="{ on: form.privacy }" @click.prevent="form.privacy = !form.privacy"><CygnusIcon name="check" v-if="form.privacy" /></span>
                <input v-model="form.privacy" type="checkbox" class="chk-native" />
                <span>I've read the <a :href="legal.privacy" target="_blank" rel="noopener">Privacy Notice</a> and understand how my data is used.</span>
              </label>
              <label class="chk">
                <span class="box" :class="{ on: form.marketing }" @click.prevent="form.marketing = !form.marketing"><CygnusIcon name="check" v-if="form.marketing" /></span>
                <input v-model="form.marketing" type="checkbox" class="chk-native" />
                <span>Send me occasional product updates <span class="muted">(optional — you can opt out anytime)</span>.</span>
              </label>
            </div>

            <div class="fbanner info">
              <span class="fb-ic"><CygnusIcon name="info-circle" /></span>
              <span><b>14-day trial — no card now.</b> Sending is capped during the trial to protect deliverability. <template v-if="liveMode">Next: payment details — no charge for 14 days.</template><template v-else>A card is required later to lift the cap and continue after 14 days.</template></span>
            </div>

            <button class="btn btn-pri" type="submit" :disabled="submitting">
              <CygnusIcon name="spinner" :spin="true" v-if="submitting" />
              {{ submitting ? 'Creating…' : 'Continue to your company →' }}
            </button>
          </form>
        </template>

        <!-- ==================== POST-START — CHECK YOUR INBOX ==================== -->
        <template v-else-if="!isCompanyStep && mode === 'sent'">
          <div class="big-state">
            <div class="bs-ic mail"><CygnusIcon name="envelope-open-text" /></div>
            <h3>Check your inbox</h3>
            <p>We've sent a verification link to <b>{{ sentEmail }}</b>. Click it to confirm your email, then you'll pick up at step 2 — your company. The link works once and expires in <b>24 hours</b>.</p>
            <div class="resend">Didn't arrive? Check spam, or <a href="#" @click.prevent="restart">start again</a>.</div>
          </div>
        </template>

        <!-- ============================ STEP 2 — COMPANY ============================ -->
        <template v-else>
          <template v-if="!regToken">
            <div class="big-state">
              <div class="bs-ic warn"><CygnusIcon name="clock" /></div>
              <h3>Verify your email first</h3>
              <p>Open the verification email we sent you and click the link — it brings you straight back here to finish setting up your company.</p>
              <div class="resend"><a href="/register">← Back to sign-up</a></div>
            </div>
          </template>

          <!-- duplicate-company guard: a workspace already exists for this CH number -->
          <template v-else-if="joinPending">
            <div class="big-state">
              <div class="bs-ic mail"><CygnusIcon name="envelope" /></div>
              <h3>Request sent</h3>
              <p>{{ joinMessage }}</p>
              <div class="resend"><a href="/login">← Back to sign in</a></div>
            </div>
          </template>

          <template v-else-if="joinMode">
            <div class="af-h">This company already has a workspace</div>
            <div class="af-sub">A Yorlin workspace already exists for <b>{{ existingWorkspace.name }}</b>.</div>
            <div class="fbanner info">
              <span class="fb-ic"><CygnusIcon name="info-circle" /></span>
              <span>You can ask the workspace owner to add you — they'll approve your request and you'll join as a <b>Campaign manager</b>. Or continue and create a separate workspace of your own.</span>
            </div>
            <div v-if="banner" class="fbanner err">
              <span class="fb-ic"><CygnusIcon name="warning" /></span>
              <span>{{ banner }}</span>
            </div>
            <button class="btn btn-pri" type="button" :disabled="submitting" @click="requestJoin">
              <CygnusIcon name="spinner" :spin="true" v-if="submitting" />
              Request to join {{ existingWorkspace.name }} →
            </button>
            <button class="btn btn-sec" type="button" style="margin-top:10px" :disabled="submitting" @click="continueAnyway">
              No — create my own workspace
            </button>
          </template>

          <template v-else>
            <div class="af-h">Find your company</div>
            <div class="af-sub">Search Companies House by name or number — we'll fill in the rest.</div>

            <div v-if="banner" class="fbanner err">
              <span class="fb-ic"><CygnusIcon name="warning" /></span>
              <span>{{ banner }}</span>
            </div>

            <!-- typeahead (hidden once a company is picked / manual chosen) -->
            <div v-if="!selected && !manual" class="ch-search">
              <div class="input" :class="{ focus: chFocus }">
                <span class="ic"><CygnusIcon name="search" /></span>
                <input
                  v-model="chQuery"
                  class="val"
                  type="text"
                  placeholder="e.g. Wharfedale Digital, or 12894571"
                  @focus="chFocus = true"
                  @blur="chFocus = false"
                  @input="onChInput"
                />
                <span v-if="chLoading" class="eye"><CygnusIcon name="spinner" :spin="true" /></span>
              </div>

              <div v-if="chResults.length" class="ch-results">
                <div class="ch-head">Companies House <span class="src"><i></i>Live register lookup</span></div>
                <div
                  v-for="c in chResults"
                  :key="c.number"
                  class="ch-row"
                  @click="pickCompany(c)"
                >
                  <div class="ch-main">
                    <div class="ch-name">
                      {{ c.title }}
                      <span class="ch-status" :class="statusClass(c.status)">{{ prettyStatus(c.status) }}</span>
                    </div>
                    <div class="ch-addr"><span class="ch-num">{{ c.number }}</span><template v-if="c.address_snippet"> · {{ c.address_snippet }}</template></div>
                  </div>
                  <div class="ch-pick"><button type="button" class="btn btn-sec btn-sm">That's us</button></div>
                </div>
              </div>
              <div v-else-if="chQuery.length >= 2 && !chLoading" class="ch-manual">
                No matches on Companies House.
              </div>

              <div class="ch-manual">Not registered, or can't find it? <a href="#" @click.prevent="enterManually">Enter details manually →</a></div>
            </div>

            <!-- confirmed / manual company card -->
            <div v-else class="conf-co">
              <div class="cc-top">
                <div class="cc-ic"><svg width="20" height="20" viewBox="0 0 64 64" fill="none" stroke-linecap="round" aria-hidden="true"><path d="M12 45 L17 39 L22 42 L27 36 L32 38 L37 32 L42 35 L47 29" stroke="currentColor" stroke-width="4" stroke-linejoin="round"/><path d="M47 29 L52 22.4" stroke="#F2B705" stroke-width="4"/></svg></div>
                <div class="cc-id">
                  <div class="cc-name">
                    <input v-if="manual" v-model.trim="companyForm.company_name" class="cc-name-input" type="text" placeholder="Your company name" />
                    <template v-else>{{ selected.title }}</template>
                  </div>
                  <div v-if="!manual" class="cc-meta">
                    <span class="ch-num">{{ selected.number }}</span> · {{ prettyStatus(selected.status) }}
                  </div>
                  <div v-else class="cc-meta">Entered manually · not matched to the register</div>
                </div>
                <a class="cc-change" href="#" @click.prevent="clearSelection">Change</a>
              </div>

              <div v-if="dissolvedWarning" class="fbanner warn cc-warn">
                <span class="fb-ic"><CygnusIcon name="warning" /></span>
                <span>This company is <b>dissolved</b> at Companies House. You can still continue if this is you.</span>
              </div>

              <div class="cc-fields">
                <div class="mini-field">
                  <div class="mf-l">Website</div>
                  <div class="mf-in"><input v-model.trim="companyForm.website" type="text" placeholder="wharfedale.co.uk" /></div>
                </div>
                <div class="mini-field">
                  <div class="mf-l">Industry / sector</div>
                  <div class="mf-in"><input v-model.trim="companyForm.sector" type="text" placeholder="e.g. Digital agency" /></div>
                </div>
                <div class="mini-field">
                  <div class="mf-l">Company size</div>
                  <div class="mf-in">
                    <select v-model="companyForm.company_size">
                      <option value="">Choose…</option>
                      <option>Just me</option>
                      <option>2–5 people</option>
                      <option>6–20 people</option>
                      <option>21–50 people</option>
                      <option>50+ people</option>
                    </select>
                  </div>
                </div>
                <div class="mini-field">
                  <div class="mf-l">What do you sell?</div>
                  <div class="mf-in"><input v-model.trim="companyForm.sells" type="text" placeholder="e.g. websites, SEO…" /></div>
                </div>
              </div>
            </div>

            <button v-if="selected || manual" class="btn btn-pri" type="button" :disabled="submitting" @click="submitCompany">
              <CygnusIcon name="spinner" :spin="true" v-if="submitting" />
              {{ submitting ? 'Setting up…' : 'Create workspace & continue →' }}
            </button>
            <div class="af-legal">
              We use these details to personalise your pipelines and suggest who to target. Nothing is shared.
              <a :href="legal.privacy" target="_blank" rel="noopener">How we use your data</a>
            </div>
          </template>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import AuthBrandPanel from '@root/components/ui/AuthBrandPanel.vue';
import useTokenStore from '@apexl/icarus-core/src/store/useTokenStore';

/* global BACKEND_DOMAIN, MARKETING_BASE_URL */

// The PUBLIC registration endpoints are called with native fetch — deliberately
// NOT the core useApi(): those endpoints are unauthenticated and a 403 (e.g. an
// expired CH token gate) must surface as data, not trigger the auth interceptor's
// login redirect. fetch never throws on a non-2xx status, so every body is read.
const DISSOLVED = ['dissolved', 'liquidation', 'converted-closed', 'removed'];

export default {
  name: 'RegistrationFlow',
  components: { AuthBrandPanel },
  data() {
    return {
      tokens: useTokenStore(),
      mode: 'account', // account | sent
      closed: false, // server answered registration_closed (REGISTRATION_ENABLED off)
      submitting: false,
      banner: '',
      fieldErr: '',
      showPw: false,
      form: { name: '', email: '', password: '', tc: false, privacy: false, marketing: false },
      sentEmail: '',
      // step 2
      regToken: '',
      chQuery: '',
      chResults: [],
      chLoading: false,
      chFocus: false,
      chTimer: null,
      selected: null,
      manual: false,
      dissolvedWarning: false,
      companyForm: { company_name: '', website: '', sector: '', company_size: '', sells: '' },
      // duplicate-company guard
      existingWorkspace: null, // { tenant, name }
      joinMode: false,
      joinPending: false,
      joinMessage: '',
    };
  },
  computed: {
    // Legal pages live on the marketing site (config-driven base). The real
    // pages: /privacy/, /terms/, /fair-sending-policy/ (Acceptable Use).
    legal() {
      const base = (typeof MARKETING_BASE_URL !== 'undefined' && MARKETING_BASE_URL)
        ? String(MARKETING_BASE_URL).replace(/\/+$/, '')
        : 'https://musubi-www.ddev.site';
      return {
        privacy: base + '/privacy/',
        terms: base + '/terms/',
        fairSending: base + '/fair-sending-policy/',
      };
    },
    isCompanyStep() {
      return this.$route.path.replace(/\/+$/, '') === '/register/company';
    },
    liveMode() {
      // Copy-only hint; the server is authoritative on dev vs live at checkout.
      return false;
    },
    pw() {
      return this.evaluatePassword(this.form.password);
    },
    panel() {
      if (this.isCompanyStep) {
        return {
          kick: 'Almost there',
          heading: 'Tell us who <em>you</em> are, so we can find who needs you.',
          sub: 'We look you up on Companies House the same way we\'ll look up your prospects — so your pipelines start from real, verified data, not a form you filled by hand.',
          bullets: '',
          foot: 'We only use this to personalise your pipelines',
        };
      }
      return {
        kick: '14-day trial',
        heading: 'Somewhere near you, a business has the exact problem <em>you fix</em>.',
        sub: 'Set up your account and we\'ll find the first ones this week — write the opener that proves you noticed, and follow up until they book a call.',
        bullets: '✓ No lists to buy   ✓ Companies House native   ✓ Cancel anytime',
        foot: 'Card required later · sending capped during trial',
      };
    },
  },
  created() {
    if (this.isCompanyStep) {
      this.regToken = String(this.$route.query.rt || '');
    }
    // Learn up front whether sign-up is open, so a closed server shows the
    // closed message instead of a form that can only fail. A token-less
    // company-search is side-effect free: when open the controller refuses it
    // before any lookup or rate-limit write; when closed the gate answers
    // registration_closed, which checkClosed() picks up.
    this.apiGet('/api/v1/register/company-search', {});
  },
  methods: {
    // ---- fetch helpers (interceptor-free, read every status) ----
    checkClosed(d) {
      if (d && d.error === 'registration_closed') this.closed = true;
      return d;
    },
    async apiPost(path, body) {
      try {
        const res = await fetch(BACKEND_DOMAIN + path, {
          method: 'POST',
          credentials: 'include',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        return this.checkClosed(await res.json().catch(() => ({})));
      } catch (e) {
        return { ok: false, error: 'network' };
      }
    },
    async apiGet(path, params) {
      try {
        const qs = new URLSearchParams(params).toString();
        const res = await fetch(BACKEND_DOMAIN + path + (qs ? '?' + qs : ''), {
          method: 'GET',
          credentials: 'include',
          headers: { Accept: 'application/json' },
        });
        return this.checkClosed(await res.json().catch(() => ({})));
      } catch (e) {
        return { ok: false, error: 'network' };
      }
    },

    // ---- password strength (client mirror of PasswordStrengthService) ----
    evaluatePassword(pw) {
      const len = pw ? pw.length : 0;
      let classes = 0;
      if (/[a-z]/.test(pw)) classes++;
      if (/[A-Z]/.test(pw)) classes++;
      if (/[0-9]/.test(pw)) classes++;
      if (/[^A-Za-z0-9]/.test(pw)) classes++;
      let score = classes;
      if (len >= 14) score++;
      if (len < 10) score = Math.min(score, 1);
      score = Math.max(0, Math.min(4, score));

      let label = 'Weak', labelClass = '';
      if (score >= 4) { label = 'Strong'; labelClass = 'strong'; }
      else if (score === 3) { label = 'Good'; labelClass = 'mid'; }
      else if (score === 2) { label = 'Fair'; labelClass = 'mid'; }

      let hint;
      if (len < 10) hint = `${len} character${len === 1 ? '' : 's'} · use at least 10`;
      else if (classes < 3) hint = `${len} characters · add a symbol to strengthen`;
      else hint = `${len} characters · mixed case · number`;

      return { score, label, labelClass, hint };
    },
    barClass(n) {
      const map = { 1: 'f1', 2: 'f2', 3: 'f3', 4: 'f4' };
      return n <= this.pw.score ? map[this.pw.score] : '';
    },

    // ---- step 1 ----
    async submitStart() {
      this.banner = '';
      this.fieldErr = '';
      if (!this.form.name) { this.fail('name', 'Please enter your full name.'); return; }
      if (!this.form.email) { this.fail('email', 'Please enter a valid work email.'); return; }
      if (!this.form.tc) { this.banner = 'Please accept the Terms of Service and Acceptable Use to continue.'; return; }
      if (!this.form.privacy) { this.banner = 'Please confirm you have read the Privacy Notice.'; return; }
      if (this.pw.score < 2 || this.form.password.length < 10) { this.fail('password', 'Use at least 10 characters with a mix of cases, a number or symbol.'); return; }

      this.submitting = true;
      try {
        const d = await this.apiPost('/api/v1/register/start', {
          name: this.form.name,
          email: this.form.email,
          password: this.form.password,
          tc_accepted: this.form.tc,
          privacy_accepted: this.form.privacy,
          marketing_opt_in: this.form.marketing,
        });
        if (d.ok) {
          this.sentEmail = d.email || this.form.email;
          this.mode = 'sent';
        } else {
          this.fieldErr = d.field || '';
          this.banner = d.error || 'Something went wrong. Please try again.';
        }
      } catch (e) {
        this.banner = 'We couldn\'t reach the server. Please try again.';
      } finally {
        this.submitting = false;
      }
    },
    fail(field, msg) { this.fieldErr = field; this.banner = msg; },
    restart() {
      this.mode = 'account';
      this.banner = '';
    },

    // ---- step 2: Companies House typeahead ----
    onChInput() {
      this.banner = '';
      if (this.chTimer) clearTimeout(this.chTimer);
      const q = this.chQuery.trim();
      if (q.length < 2) { this.chResults = []; this.chLoading = false; return; }
      this.chLoading = true;
      this.chTimer = setTimeout(this.runChSearch, 280);
    },
    async runChSearch() {
      const q = this.chQuery.trim();
      if (q.length < 2) { this.chLoading = false; return; }
      try {
        const d = await this.apiGet('/api/v1/register/company-search', { q, rt: this.regToken });
        this.chResults = d.ok && Array.isArray(d.results) ? d.results : [];
      } catch (e) {
        this.chResults = [];
      } finally {
        this.chLoading = false;
      }
    },
    pickCompany(c) {
      this.selected = c;
      this.manual = false;
      this.companyForm.company_name = c.title;
      this.dissolvedWarning = DISSOLVED.includes(String(c.status || '').toLowerCase());
      this.chResults = [];
    },
    enterManually() {
      this.manual = true;
      this.selected = null;
      this.dissolvedWarning = false;
    },
    clearSelection() {
      this.selected = null;
      this.manual = false;
      this.dissolvedWarning = false;
      this.chQuery = '';
      this.chResults = [];
    },
    statusClass(s) {
      return DISSOLVED.includes(String(s || '').toLowerCase()) ? 'dissolved' : 'active';
    },
    prettyStatus(s) {
      const v = String(s || '').toLowerCase();
      if (!v) return 'Unknown';
      return v.charAt(0).toUpperCase() + v.slice(1).replace(/-/g, ' ');
    },

    // ---- step 2: submit → company then checkout ----
    async submitCompany() {
      this.banner = '';
      const name = this.selected ? this.selected.title : this.companyForm.company_name;
      if (!name && !(this.selected && this.selected.number)) {
        this.banner = 'Tell us your company name, or enter details manually.';
        return;
      }
      this.submitting = true;
      try {
        const companyPayload = {
          company_name: name,
          company_number: this.selected ? this.selected.number : '',
          company_status: this.selected ? this.selected.status : '',
          website: this.companyForm.website,
          sector: this.companyForm.sector,
          company_size: this.companyForm.company_size,
          sells: this.companyForm.sells,
        };
        const cd = await this.apiPost('/api/v1/register/company', {
          reg_token: this.regToken,
          company: companyPayload,
        });
        if (!cd.ok) {
          this.banner = cd.error || 'We couldn\'t save your company. Please try again.';
          this.submitting = false;
          return;
        }

        // Duplicate-company guard: a workspace already exists for this CH number.
        if (cd.existing_workspace) {
          this.existingWorkspace = cd.existing_workspace;
          this.joinMode = true;
          this.submitting = false;
          return;
        }

        await this.doCheckout();
      } catch (e) {
        this.banner = 'We couldn\'t reach the server. Please try again.';
        this.submitting = false;
      }
    },

    // ---- checkout (dev: provision + login; live: hosted checkout) ----
    async doCheckout() {
      const kd = await this.apiPost('/api/v1/register/checkout', { reg_token: this.regToken });
      if (!kd.ok) {
        this.banner = kd.error || 'We couldn\'t finish setting up. Please try again.';
        this.submitting = false;
        return;
      }
      if (kd.mode === 'live' && kd.hosted_url) {
        window.location.assign(kd.hosted_url);
        return;
      }
      // Dev mode — tenant provisioned + session established server-side.
      if (kd.access_token) this.tokens.setAccessToken(kd.access_token);
      if (kd.refresh_token) this.tokens.setRefreshToken(kd.refresh_token);
      this.$router.push(kd.redirect || '/');
    },

    // ---- duplicate-company: request to join / continue anyway ----
    async requestJoin() {
      this.banner = '';
      this.submitting = true;
      try {
        const d = await this.apiPost('/api/v1/register/join-request', {
          reg_token: this.regToken,
          tenant: this.existingWorkspace.tenant,
        });
        if (d.ok) {
          this.joinMessage = d.message || 'The workspace owner has been asked to approve your request. We\'ll email you when they respond.';
          this.joinPending = true;
        } else {
          this.banner = d.error || 'We couldn\'t send your request. Please try again.';
        }
      } catch (e) {
        this.banner = 'We couldn\'t reach the server. Please try again.';
      } finally {
        this.submitting = false;
      }
    },
    async continueAnyway() {
      this.joinMode = false;
      this.existingWorkspace = null;
      this.submitting = true;
      try {
        await this.doCheckout();
      } catch (e) {
        this.banner = 'We couldn\'t reach the server. Please try again.';
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.reg-split {
  display: grid;
  grid-template-columns: 44% 56%;
  min-height: 100vh;
  background: #fff;
}
.auth-form {
  background: #fff;
  padding: 44px 48px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.af-inner {
  width: 100%;
  margin: 0 auto;
}
@media (max-width: 980px) {
  .reg-split { grid-template-columns: 1fr; }
  .reg-split :deep(.auth-brand) { display: none; }
  .auth-form { padding: 32px 22px; }
}
/* native checkbox is the a11y source of truth; the styled box mirrors it */
.chk-native {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}
.cc-name-input {
  border: 0;
  outline: 0;
  font: inherit;
  font-weight: 700;
  width: 100%;
  background: transparent;
}
.muted { color: var(--m-text-tertiary, #94A3B8); }
</style>
